import { useEffect, useState } from 'react';

declare global {
  interface Window {
    OneTrust: any;
  }
}

type ErrorState = ErrorEvent | null;

const IS_BROWSER = typeof window !== 'undefined' && typeof window.document !== 'undefined';
const ONE_TRUST_SDK_URL = process.env.REACT_APP_RBI_ONETRUST_COOKIE_COMPLIANCE_SDK_URL ?? '';
const ONE_TRUST_APPLICATION_ID = process.env.REACT_APP_RBI_ONETRUST_COOKIE_COMPLIANCE_SDK_ID ?? '';
const ONE_TRUST_SDK_SCRIPT_ID = 'one-trust-cookie-consent-sdk';

/**
 * Loads one trust sdk cookie consent script
 */
export default function useOneTrustSdk() {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<ErrorState>(null);

  useEffect(() => {
    if (!IS_BROWSER || !ONE_TRUST_SDK_URL || !ONE_TRUST_APPLICATION_ID) {
      return;
    }

    const existing = document.querySelectorAll(`script#${ONE_TRUST_SDK_SCRIPT_ID}`);

    if (existing.length > 0) {
      setLoading(false);
      setLoaded(true);
      return;
    }

    const handleLoad = () => {
      setLoading(false);
      setLoaded(true);

      // This code block is required by One Trust script which is called after the SDK is loaded
      function OptanonWrapper() {} // eslint-disable-line @typescript-eslint/no-unused-vars
    };

    const handleError = (err: ErrorState) => {
      setLoading(false);
      setLoaded(false);
      setError(err);
    };

    const otSdkScript = document.createElement('script');
    // TODO: This will be updated to include live endpoints based on region.
    otSdkScript.setAttribute('src', ONE_TRUST_SDK_URL);
    otSdkScript.setAttribute('data-domain-script', ONE_TRUST_APPLICATION_ID);
    otSdkScript.setAttribute('id', ONE_TRUST_SDK_SCRIPT_ID);
    otSdkScript.setAttribute('type', 'text/javascript');
    otSdkScript.setAttribute('charset', 'UTF-8');
    otSdkScript.setAttribute('crossOrigin', 'anonymous');
    otSdkScript.async = true;
    otSdkScript.addEventListener('load', handleLoad);
    otSdkScript.addEventListener('error', handleError);

    document.body.appendChild(otSdkScript);

    return () => {
      document.body.removeChild(otSdkScript);
      otSdkScript.removeEventListener('load', handleLoad);
      otSdkScript.removeEventListener('error', handleError);
    };
  }, []);

  return { loaded, loading, error };
}
